import React from "react";
import ModalButton from "../ModalButton";
import { WarningCircleIcon } from "@phosphor-icons/react";
import {
  StyledPopup,
  Content,
  IconWrap,
  Title,
  Intro,
  BottomCard,
} from "../MarcaBlancaRequirementsModal/styles";
import {
  Sections,
  Section,
  SectionTitle,
  ItemsList,
  Item,
  PriceDetail,
} from "./styles";
import CurrencyFormat from "../../utils/currencyFormat";
import { language } from "../../utils/language";
import { CartValidationResult } from "../../utils/cartValidation";

type Props = {
  open: boolean;
  color: string;
  defaultLanguage: string;
  result: CartValidationResult | null;
  onAccept: () => void;
};

const CartValidationModal: React.FC<Props> = ({
  open,
  color,
  defaultLanguage,
  result,
  onAccept,
}) => {
  if (!result) return null;

  const texts: any = language[defaultLanguage];

  const sections = [
    {
      title: texts["cartValidationInactiveProducts"],
      items: result.inactiveProducts,
    },
    {
      title: texts["cartValidationInactiveCategories"],
      items: result.inactiveCategories,
    },
    {
      title: texts["cartValidationMissingReferences"],
      items: result.missingReferences,
    },
  ].filter((section) => section.items.length > 0);

  const hasDetail = sections.length > 0 || result.changedPrices.length > 0;

  return (
    <StyledPopup
      open={open}
      modal
      closeOnDocumentClick={false}
      closeOnEscape={false}
      onClose={() => undefined}
    >
      <div className="container">
        <Content>
          <IconWrap>
            <WarningCircleIcon size={56} weight="regular" color="#F5C518" />
          </IconWrap>
          <Title>
            {result.inactiveStore
              ? texts["cartValidationStoreTitle"]
              : texts["cartValidationTitle"]}
          </Title>
          <Intro style={{ fontSize: "15px", lineHeight: 1.4 }}>
            {result.inactiveStore
              ? texts["cartValidationStoreMessage"]
              : hasDetail
              ? texts["cartValidationIntro"]
              : texts["cartValidationGenericMessage"]}
          </Intro>

          {!result.inactiveStore && hasDetail && (
            <Sections>
              {sections.map((section) => (
                <Section key={section.title}>
                  <SectionTitle>{section.title}</SectionTitle>
                  <ItemsList>
                    {section.items.map((item) => (
                      <Item key={`${section.title}-${item.id}`}>
                        {item.name}
                      </Item>
                    ))}
                  </ItemsList>
                </Section>
              ))}

              {result.changedPrices.length > 0 && (
                <Section>
                  <SectionTitle>
                    {texts["cartValidationChangedPrices"]}
                  </SectionTitle>
                  <ItemsList>
                    {result.changedPrices.map((item) => (
                      <Item key={`price-${item.id}`}>
                        {item.name}
                        <PriceDetail>
                          {texts["cartValidationPriceBefore"]}{" "}
                          <CurrencyFormat amount={item.previousAmount} />
                          {" · "}
                          {texts["cartValidationPriceNow"]}{" "}
                          <CurrencyFormat amount={item.currentAmount} />
                        </PriceDetail>
                      </Item>
                    ))}
                  </ItemsList>
                </Section>
              )}
            </Sections>
          )}
        </Content>

        <BottomCard>
          <ModalButton color={color} secondary={false} onClick={onAccept}>
            {texts["cartValidationAccept"]}
          </ModalButton>
        </BottomCard>
      </div>
    </StyledPopup>
  );
};

export default CartValidationModal;
